import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { PLAYER_COLORS } from '../../../utils/constants.js';
import { colored, seededRandom } from '../../Style.js';
import { SCIFI, metal, hashId } from './style.js';

// Neutral/claimable zones glow a cool white; unaffordable ones go dark
const NEUTRAL_COLOR = '#d6e2ee';
const DIMMED_COLOR = '#4a5160';
const CURTAIN_HEIGHT = 1.8;

// A fabricator: a hex pad with a glowing ring in the owner's colour and a
// rotating hologram of the unit for sale. Forward-base claim zones are a wide
// ring of emitter posts joined by a light curtain.
export class BuyZone {
  constructor(scene, zone) {
    this.scene = scene;
    this.zone = zone;
    this.mesh = null; // Root group, positioned at the zone centre
    this.label = null;
    this.glowMesh = null;
    this.inlayMesh = null;
    this.iconMesh = null;
    this.iconMaterials = [];
    this.teamMaterials = [];
    this.canAfford = true;
    this.baseColor = this.getZoneColor(zone.ownerId);
    this.rand = seededRandom(hashId(zone.id));
    this.phase = this.rand() * Math.PI * 2;
    this.uniforms = { uTime: { value: 0 }, uColor: { value: new THREE.Color(this.baseColor) }, uStrength: { value: 1 } };
    this.create();
  }

  getZoneColor(ownerId) {
    if (ownerId === -1) {
      return NEUTRAL_COLOR;
    }
    return PLAYER_COLORS[ownerId];
  }

  create() {
    this.mesh = new THREE.Group();
    // A hair of per-zone lift so overlapping circles don't z-fight
    const baseY = (this.zone.position.y || 0) + this.rand() * 0.03;
    this.mesh.position.set(this.zone.position.x, baseY, this.zone.position.z);

    if (this.zone.unitType) {
      this.createFabricator();
    } else {
      this.createClaimRing(baseY);
    }

    this.scene.add(this.mesh);
  }

  // Unlit material that follows the owner's colour
  teamMaterial(opts = {}) {
    const material = new THREE.MeshBasicMaterial({
      color: this.baseColor,
      transparent: true,
      opacity: opts.opacity ?? 1,
      blending: opts.additive ? THREE.AdditiveBlending : THREE.NormalBlending,
      depthWrite: false,
      side: opts.side ?? THREE.FrontSide,
      toneMapped: false
    });
    this.teamMaterials.push(material);
    return material;
  }

  createFabricator() {
    const radius = this.zone.radius;
    const pad = radius - 0.35;
    const parts = [];

    // Hex pad with a raised rim, an inner deck plate and bollards at the corners
    parts.push(colored(new THREE.CylinderGeometry(pad, pad + 0.15, 0.16, 6), SCIFI.alloyDark, { y: 0.08 }));
    parts.push(colored(new THREE.CylinderGeometry(pad - 0.9, pad - 0.9, 0.04, 6), SCIFI.alloy, { y: 0.18 }));
    parts.push(colored(new THREE.CylinderGeometry(1.05, 1.2, 0.3, 12), SCIFI.alloyLight, { y: 0.2 }));
    // Hex corners, matching the cylinder's and ring's vertices
    const corners = [];
    for (let i = 0; i < 6; i++) {
      const c = Math.PI / 6 + (i / 6) * Math.PI * 2;
      corners.push([Math.cos(c) * (pad - 0.15), Math.sin(c) * (pad - 0.15), c]);
    }
    for (const [x, z, c] of corners) {
      parts.push(colored(new THREE.BoxGeometry(0.4, 0.45, 0.4), SCIFI.alloyDark, { x, y: 0.3, z, ry: -c }));
    }
    const base = new THREE.Mesh(mergeGeometries(parts), metal(0xffffff, { vertexColors: true }));
    base.receiveShadow = true;
    base.castShadow = true;
    this.mesh.add(base);

    // Glowing ring and tick marks in the owner's colour
    const ring = [colored(new THREE.RingGeometry(pad - 0.62, pad - 0.38, 6, 1, Math.PI / 6), 0xffffff, { rx: -Math.PI / 2, y: 0.19 })];
    corners.forEach(([x, z, c], i) => {
      ring.push(colored(new THREE.BoxGeometry(0.1, 0.02, 0.6), 0xffffff, { z: pad - 1.3, y: 0.205 }).rotateY((i / 6) * Math.PI * 2));
      // Small lamp on top of each bollard
      ring.push(colored(new THREE.BoxGeometry(0.42, 0.06, 0.42), 0xffffff, { x, y: 0.55, z, ry: -c }));
    });
    this.glowMesh = new THREE.Mesh(mergeGeometries(ring), this.teamMaterial());
    this.mesh.add(this.glowMesh);

    // Emitter lens in the centre of the pad
    this.inlayMesh = new THREE.Mesh(new THREE.CylinderGeometry(0.8, 0.8, 0.05, 16), new THREE.MeshStandardMaterial({
      color: this.baseColor,
      emissive: this.baseColor,
      emissiveIntensity: 1.2,
      roughness: 0.4
    }));
    this.inlayMesh.position.y = 0.37;
    this.mesh.add(this.inlayMesh);

    // Projection cone rising from the lens
    const cone = new THREE.Mesh(new THREE.CylinderGeometry(1.5, 0.75, 2.4, 20, 1, true), this.teamMaterial({ additive: true, opacity: 0.12, side: THREE.DoubleSide }));
    cone.position.y = 0.4 + 1.2;
    this.mesh.add(cone);
    this.cone = cone;

    this.createUnitIcon(0.55);
  }

  createClaimRing(baseY) {
    const radius = this.zone.radius - 0.6;
    const posts = 16;
    const parts = [];
    const tips = [];
    // Posts reach down to the ground even when the zone sits on a platform
    const groundY = -baseY;
    const top = CURTAIN_HEIGHT + 0.6;
    for (let i = 0; i < posts; i++) {
      const a = (i / posts) * Math.PI * 2;
      const x = Math.cos(a) * radius;
      const z = Math.sin(a) * radius;
      const height = top - groundY;
      parts.push(colored(new THREE.BoxGeometry(0.45, height, 0.45), SCIFI.alloyDark, { x, y: groundY + height / 2, z, ry: -a }));
      parts.push(colored(new THREE.BoxGeometry(0.7, 0.3, 0.7), SCIFI.alloy, { x, y: groundY + 0.15, z, ry: -a }));
      parts.push(colored(new THREE.BoxGeometry(0.6, 0.18, 0.6), SCIFI.alloyLight, { x, y: top, z, ry: -a }));
      tips.push(colored(new THREE.BoxGeometry(0.5, 0.2, 0.5), 0xffffff, { x, y: top + 0.18, z, ry: -a }));
      tips.push(colored(new THREE.BoxGeometry(0.12, height - 0.6, 0.48), 0xffffff, { x, y: groundY + height / 2, z, ry: -a }));
    }
    const postMesh = new THREE.Mesh(mergeGeometries(parts), metal(0xffffff, { vertexColors: true }));
    postMesh.castShadow = true;
    postMesh.receiveShadow = true;
    this.mesh.add(postMesh);

    // A ring of light floating at the top of the posts, plus one on the ground when there is ground
    tips.push(colored(new THREE.TorusGeometry(radius, 0.07, 4, 96), 0xffffff, { rx: Math.PI / 2, y: top + 0.05 }));
    if (baseY < 0.5) {
      tips.push(colored(new THREE.RingGeometry(radius - 0.25, radius + 0.25, 96), 0xffffff, { rx: -Math.PI / 2, y: 0.05 }));
    }
    this.glowMesh = new THREE.Mesh(mergeGeometries(tips), this.teamMaterial());
    this.mesh.add(this.glowMesh);

    // Light curtain between the posts, fading upwards and scanning slowly
    const curtain = new THREE.Mesh(
      new THREE.CylinderGeometry(radius, radius, CURTAIN_HEIGHT, 96, 1, true),
      new THREE.ShaderMaterial({
        uniforms: this.uniforms,
        transparent: true,
        depthWrite: false,
        side: THREE.DoubleSide,
        blending: THREE.AdditiveBlending,
        vertexShader: /* glsl */`
          varying vec2 vUv;
          void main() {
            vUv = uv;
            gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
          }
        `,
        fragmentShader: /* glsl */`
          uniform float uTime;
          uniform vec3 uColor;
          uniform float uStrength;
          varying vec2 vUv;
          void main() {
            float fade = pow(1.0 - vUv.y, 1.5);
            float stripes = 0.7 + 0.3 * sin(vUv.x * 160.0 - uTime * 2.0);
            float sweep = pow(0.5 + 0.5 * sin(vUv.x * 6.2831 * 2.0 - uTime * 0.9), 6.0);
            gl_FragColor = vec4(uColor * (fade * 0.55 * stripes + sweep * fade * 0.5) * uStrength, 1.0);
          }
        `
      })
    );
    curtain.position.y = CURTAIN_HEIGHT / 2 + 0.05;
    curtain.renderOrder = 2;
    this.curtain = curtain;
    this.mesh.add(curtain);
  }

  createUnitIcon(baseY) {
    const unitType = this.zone.unitType;
    if (!unitType) return; // No icon for base zones

    const builders = {
      tank: hoverTank,
      super_tank: siegeWalker,
      airplane: gunshipDrone,
      super_helicopter: dreadnought,
      sniper: railgunner,
      rocket_launcher: siegeDrone
    };
    const build = builders[unitType];
    if (!build) return;

    const geometry = mergeGeometries(build());
    const icon = new THREE.Group();

    // Translucent body with bright edges: a classic hologram
    const fill = this.teamMaterial({ additive: true, opacity: 0.32, side: THREE.DoubleSide });
    const edges = new THREE.LineSegments(new THREE.EdgesGeometry(geometry, 25), new THREE.LineBasicMaterial({
      color: new THREE.Color(this.baseColor).lerp(new THREE.Color(0xffffff), 0.35),
      transparent: true,
      opacity: 0.95,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      toneMapped: false
    }));
    this.teamMaterials.push(edges.material);
    edges.material.userData.whiten = 0.35;
    icon.add(new THREE.Mesh(geometry, fill), edges);
    fill.userData.baseOpacity = 0.32;
    edges.material.userData.baseOpacity = 0.95;
    this.iconMaterials.push(fill, edges.material);

    // A scan ring sweeping up and down the hologram
    const scan = new THREE.Mesh(new THREE.TorusGeometry(1.0, 0.03, 4, 32), this.teamMaterial({ additive: true, opacity: 0.8 }));
    scan.rotation.x = Math.PI / 2;
    scan.material.userData.baseOpacity = 0.8;
    this.iconMaterials.push(scan.material);
    this.scan = scan;
    this.mesh.add(scan);

    icon.scale.setScalar(unitType.startsWith('super_') ? 1.25 : 1.0);
    icon.position.y = baseY;
    icon.userData.baseY = baseY;
    this.mesh.add(icon);
    this.iconMesh = icon;
  }

  // Update for animation (pulsing ring, turning hologram)
  update(time) {
    this.uniforms.uTime.value = time;

    if (this.glowMesh) {
      if (this.canAfford) {
        this.glowMesh.material.opacity = 0.75 + Math.sin(time * 3 + this.phase) * 0.25;
      } else {
        // Dimmed when can't afford
        this.glowMesh.material.opacity = 0.2;
      }
    }

    if (this.iconMesh) {
      this.iconMesh.rotation.y = time * 0.8 + this.phase;
      this.iconMesh.position.y = this.iconMesh.userData.baseY + (Math.sin(time * 1.6 + this.phase) * 0.5 + 0.5) * 0.15;
      // Occasional hologram glitch
      const glitch = Math.sin(time * 23.0 + this.phase * 7) > 0.97 && this.canAfford;
      this.iconMesh.position.x = glitch ? 0.06 : 0;
      this.iconMesh.scale.x = this.iconMesh.scale.z * (glitch ? 1.08 : 1);
    }
    if (this.scan) {
      const s = (Math.sin(time * 1.3 + this.phase) * 0.5 + 0.5);
      this.scan.position.y = 0.6 + s * 1.7;
      this.scan.scale.setScalar(1.15 - s * 0.35);
    }
  }

  // Set whether the player can afford this zone's unit
  setAffordable(canAfford) {
    if (this.canAfford === canAfford) return;

    this.canAfford = canAfford;

    if (this.inlayMesh) {
      const color = canAfford ? this.baseColor : DIMMED_COLOR;
      this.inlayMesh.material.color.set(color);
      this.inlayMesh.material.emissive.set(color);
      this.inlayMesh.material.emissiveIntensity = canAfford ? 1.2 : 0;
    }
    for (const material of this.iconMaterials) {
      material.opacity = material.userData.baseOpacity * (canAfford ? 1.0 : 0.25);
    }
    if (this.cone) this.cone.visible = canAfford;
    this.uniforms.uStrength.value = canAfford ? 1 : 0.3;
  }

  // Check if a position is within this buy zone
  isInRange(position) {
    const dx = position.x - this.zone.position.x;
    const dz = position.z - this.zone.position.z;
    const distSq = dx * dx + dz * dz;
    return distSq <= this.zone.radius * this.zone.radius;
  }

  // Update the owner of this zone (when claimed)
  updateOwner(newOwnerId) {
    if (this.zone.ownerId === newOwnerId) return;

    this.zone.ownerId = newOwnerId;
    this.baseColor = this.getZoneColor(newOwnerId);

    const white = new THREE.Color(0xffffff);
    for (const material of this.teamMaterials) {
      material.color.set(this.baseColor).lerp(white, material.userData.whiten || 0);
    }
    this.uniforms.uColor.value.set(this.baseColor);
    if (this.inlayMesh && this.canAfford) {
      this.inlayMesh.material.color.set(this.baseColor);
      this.inlayMesh.material.emissive.set(this.baseColor);
    }
  }

  remove() {
    if (!this.mesh) return;
    this.scene.remove(this.mesh);
    this.mesh.traverse(child => {
      if (child.isMesh || child.isLineSegments) child.geometry.dispose();
    });
    this.teamMaterials.forEach(material => material.dispose());
    if (this.inlayMesh) this.inlayMesh.material.dispose();
    if (this.curtain) this.curtain.material.dispose();
  }
}

// --- Hologram silhouettes, built facing +Z on the pad. Colour is ignored. ---

const W = 0xffffff;

// Hover Tank: wedge hull on four hover pads with a long-barrelled turret
function hoverTank() {
  return [
    colored(new THREE.BoxGeometry(1.2, 0.32, 1.6), W, { y: 0.55 }),
    colored(new THREE.CylinderGeometry(0.01, 0.6, 0.6, 4, 1), W, { rx: Math.PI / 2, ry: Math.PI / 4, y: 0.55, z: 1.05, scale: [1.4, 1, 0.5] }),
    colored(new THREE.BoxGeometry(0.72, 0.3, 0.8), W, { y: 0.86, z: -0.1 }),
    colored(new THREE.CylinderGeometry(0.06, 0.06, 1.2, 6), W, { rx: Math.PI / 2, y: 0.88, z: 0.75 }),
    ...[[-0.55, -0.6], [0.55, -0.6], [-0.55, 0.6], [0.55, 0.6]].map(([x, z]) =>
      colored(new THREE.CylinderGeometry(0.3, 0.3, 0.1, 10), W, { x, y: 0.3, z }))
  ];
}

// Siege Walker: armoured body on four legs with twin cannons
function siegeWalker() {
  const parts = [
    colored(new THREE.BoxGeometry(1.1, 0.5, 1.2), W, { y: 1.25 }),
    colored(new THREE.BoxGeometry(0.7, 0.3, 0.6), W, { y: 1.6, z: 0.1 })
  ];
  for (const sx of [-1, 1]) {
    parts.push(colored(new THREE.CylinderGeometry(0.06, 0.06, 1.1, 6), W, { rx: Math.PI / 2, x: sx * 0.2, y: 1.62, z: 0.85 }));
    for (const sz of [-1, 1]) {
      parts.push(colored(new THREE.BoxGeometry(0.14, 0.8, 0.14), W, { x: sx * 0.75, y: 1.15, z: sz * 0.55, rz: sx * 0.6 }));
      parts.push(colored(new THREE.BoxGeometry(0.14, 0.9, 0.14), W, { x: sx * 0.95, y: 0.45, z: sz * 0.6 }));
    }
  }
  return parts;
}

// Gunship Drone: a central pod with four ducted rotors
function gunshipDrone() {
  const parts = [
    colored(new THREE.SphereGeometry(0.38, 10, 6), W, { y: 1.1, scale: [1, 0.7, 1.5] }),
    colored(new THREE.BoxGeometry(0.1, 0.1, 0.6), W, { y: 0.95, z: 0.6 })
  ];
  for (const [x, z] of [[-0.7, -0.6], [0.7, -0.6], [-0.7, 0.6], [0.7, 0.6]]) {
    parts.push(colored(new THREE.BoxGeometry(0.08, 0.06, 0.9), W, { x: x / 2, y: 1.1, z: z / 2, ry: Math.atan2(x, z) }));
    parts.push(colored(new THREE.TorusGeometry(0.32, 0.04, 4, 16), W, { rx: Math.PI / 2, x, y: 1.15, z }));
  }
  return parts;
}

// Dreadnought: a long capital ship with swept wings and engine pods
function dreadnought() {
  const parts = [
    colored(new THREE.CylinderGeometry(0.05, 0.42, 2.2, 6), W, { rx: Math.PI / 2, y: 1.2 }),
    colored(new THREE.BoxGeometry(0.5, 0.3, 0.8), W, { y: 1.45, z: -0.4 })
  ];
  for (const side of [-1, 1]) {
    const shape = new THREE.Shape([new THREE.Vector2(0, 0.4), new THREE.Vector2(side * 1.1, -0.5), new THREE.Vector2(side * 1.1, -0.8), new THREE.Vector2(0, -0.6)]);
    parts.push(colored(new THREE.ExtrudeGeometry(shape, { depth: 0.05, bevelEnabled: false }), W, { rx: Math.PI / 2, y: 1.2 }));
    parts.push(colored(new THREE.CylinderGeometry(0.16, 0.16, 0.7, 8), W, { rx: Math.PI / 2, x: side * 0.95, y: 1.15, z: -0.5 }));
  }
  return parts;
}

function figure(parts) {
  for (const x of [-0.13, 0.13]) {
    parts.push(colored(new THREE.BoxGeometry(0.16, 0.7, 0.2), W, { x, y: 0.35 }));
  }
  parts.push(colored(new THREE.BoxGeometry(0.55, 0.65, 0.32), W, { y: 1.05 }));
  parts.push(colored(new THREE.SphereGeometry(0.17, 8, 6), W, { y: 1.55 }));
  parts.push(colored(new THREE.BoxGeometry(0.26, 0.08, 0.06), W, { y: 1.57, z: 0.15 }));
  return parts;
}

// Railgunner: a soldier holding a rifle longer than they are tall
function railgunner() {
  const parts = figure([]);
  parts.push(colored(new THREE.BoxGeometry(0.1, 0.12, 1.8), W, { x: 0.22, y: 1.15, z: 0.35 }));
  parts.push(colored(new THREE.BoxGeometry(0.16, 0.16, 0.3), W, { x: 0.22, y: 1.28, z: 0.2 }));
  return parts;
}

// Siege Drone: a squat armoured hover platform on four thruster pods,
// carrying a plasma mortar angled up over its nose
function siegeDrone() {
  const parts = [
    colored(new THREE.CylinderGeometry(0.75, 0.9, 0.42, 6), W, { y: 0.75, ry: Math.PI / 6, scale: [1, 1, 1.15] }),
    colored(new THREE.BoxGeometry(0.9, 0.12, 1.3), W, { y: 0.48 }),
    colored(new THREE.CylinderGeometry(0.42, 0.5, 0.2, 10), W, { y: 1.06 }),
    colored(new THREE.BoxGeometry(0.5, 0.36, 0.5), W, { y: 1.3, z: -0.05 })
  ];
  // Thruster pods on short struts at each corner
  for (const sx of [-1, 1]) {
    for (const sz of [-1, 1]) {
      parts.push(colored(new THREE.BoxGeometry(0.4, 0.08, 0.1), W, { x: sx * 0.75, y: 0.62, z: sz * 0.55, ry: sx * sz * 0.6 }));
      parts.push(colored(new THREE.CylinderGeometry(0.2, 0.26, 0.3, 8), W, { x: sx * 1.0, y: 0.5, z: sz * 0.72 }));
      parts.push(colored(new THREE.CylinderGeometry(0.16, 0.05, 0.22, 8), W, { x: sx * 1.0, y: 0.25, z: sz * 0.72 }));
    }
  }
  // Mortar: a wide barrel tilted forward, with a recoil collar and a plasma round at the muzzle
  const tilt = 0.55;
  const along = (d) => [0, 1.35 + Math.cos(tilt) * d, Math.sin(tilt) * d];
  const [, by, bz] = along(0.5);
  parts.push(colored(new THREE.CylinderGeometry(0.2, 0.24, 1.0, 10), W, { rx: tilt, y: by, z: bz }));
  const [, cy, cz] = along(0.25);
  parts.push(colored(new THREE.CylinderGeometry(0.3, 0.3, 0.16, 10), W, { rx: tilt, y: cy, z: cz }));
  const [, my, mz] = along(1.12);
  parts.push(colored(new THREE.SphereGeometry(0.17, 8, 6), W, { y: my, z: mz }));
  return parts;
}
